import styles from './style.module.css';

import { KeyboardEvent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector}  from '../../module/redux/hooks';
import { actions } from '../Snackbar/slice';
import { SNACKBAR_TYPE_WARNING } from '../Snackbar';
import { CardType } from '../../page/Card/slice';
import apiClient from '../../service/apiClient';

type ThisComponentProps = {
    type: CardType;
    records: CommonRecord[];
    setLoading(loading: boolean): void;
    onFilter(records: CommonRecord[]): void;
}

const defFilter: KmrFilter = {start_date: '', end_date: ''};
const categoryComponents = ['pressure', 'smoke', 'activity', 'feeling'];

const PanelFilter = ({type, records, setLoading, onFilter}: ThisComponentProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { categories, features } = useAppSelector(state => state.cardPage);

    const [text, setText] = useState('');
    const [filter, setFilter] = useState<KmrFilter>(defFilter);
    const [filteredCount, setFilteredCount] = useState(records.length);

    useEffect(() => {
        let _records = structuredClone(records);

        if (filter.components) {
            const fIds: number[] = [];
            for (const f of Object.values(features)) {
                if (!categoryComponents.includes(f.name) && filter.components.includes(f.name)) {
                    fIds.push(f.id);
                }
            }

            for (const category of categories) {
                for (const child of category.children) {
                    if (categoryComponents.includes(child.name) && filter.components.includes(child.name)) {
                        fIds.push(...child.features.map(f => f.id));
                    }
                }
            }

            _records = _records.map(r => {
                r.values = r.values?.filter(v =>
                    fIds.includes(v.feature_id)
                );
                return r;
            }).filter(r => r.values?.length);
        }

        if (filter.start_date) {
            const d = +new Date(filter.start_date);
            _records = _records.filter(r =>
                +new Date(r.record_at) >= d
            );
        }

        if (filter.end_date) {
            const d = +new Date(filter.end_date);
            _records = _records.filter(r =>
                +new Date(r.record_at) <= d
            );
        }

        setFilteredCount(_records.length);
        onFilter(_records);
    }, [categories, features, records, filter]);

    const getFilter = () => {
        setLoading(true);

        apiClient.genBloodFilter(text.trim()).then(filters => {
            const componentSet = new Set<string>();
            const filter: KmrFilter = {};

            for (const f of filters) {
                f.components?.forEach(c => componentSet.add(c));
                if (f.start_date) filter.start_date = f.start_date;
                if (f.end_date) filter.end_date = f.end_date;
            }

            if (componentSet.size > 0) {
                filter.components = Array.from(componentSet);
            }

            setText('');
            setFilter(filter);
        }, e => {
            console.log(e);
            dispatch(actions.showSnackbar({type: SNACKBAR_TYPE_WARNING, message: t('messages.warning.server_unavailable')}));
        }).finally(() => setLoading(false));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (text.trim()) {
                getFilter();
            }
        }
    };

    const setStartDate = (date?: string) => {
        const _f = structuredClone(filter);
        _f.start_date = date;
        setFilter(_f);
    };

    const setEndDate = (date?: string) => {
        const _f = structuredClone(filter);
        _f.end_date = date;
        setFilter(_f);
    };

    const removeFilterComponent = (index: number) => {
        const _f = structuredClone(filter);
        if (_f.components) {
            _f.components.splice(index, 1);
        }
        if (!_f.components?.length) {
            delete _f.components;
        }
        setFilter(_f);
    };

    return <>
        <div className={styles.wrap}>
            <div className={styles.head}>
                <div className={styles.icon}></div>
                <div className={styles.title}>{t('ai_filter.title')}</div>
                <div className={styles.text}>&mdash; {t('ai_filter.text')}</div>
            </div>
            <textarea className={styles.field} rows={4}
                value={text} onKeyDown={handleKeyDown}
                onChange={(e) => setText(e.target.value)}
                placeholder={t(`ai_filter.eq_prompt_${type}`)}
            ></textarea>
            <div className={styles.filtersWrap}>
                <b className={styles.filterTitle}>{t('ai_filter.filters')}:</b>

                <div className={styles.filters}>
                    <div className={styles.filter}>
                        {t('ai_filter.from')}:
                        <input type={'date'} value={filter.start_date ?? ''}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className={styles.filter}>
                        {t('ai_filter.to')}:
                        <input type={'date'} value={filter.end_date ?? ''}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    {filter.components?.map((c, i) =>
                        <div key={i} className={styles.filter}>
                            {t('ai_filter.indicator')}: {t(c)}
                            <div className={styles.removeFilter}
                                onClick={() => removeFilterComponent(i)}
                            ></div>
                        </div>
                    )}
                </div>

                <div className={styles.clear} onClick={() => setFilter(defFilter)}>{t('ai_filter.clear')}</div>
            </div>
        </div>
        <div className={styles.items}>
            <Trans
                i18nKey="ai_filter.count_records"
                values={{ current: filteredCount, all: records.length }}
                components={[
                    <b key="0" className={styles.current} />,
                    <b key="1" />
                ]}
            />
        </div>
    </>;
};

export default PanelFilter;